import {
  BreakPointCondition,
  UI,
  useBreakpointIf,
  useLoading,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { BoxBorder, Typography } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { InteractionChannelAuditRequest } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box } from 'native-base';
import React, { useEffect, useState } from 'react';

import { useToastCreator } from '../../../../../../../../hooks/useToastCreator';
import { useBatchTicketActions } from '../../../../../../../../store/batchTicket/actions';
import {
  errorMessageBatchTicketRequestItem,
  successAddInteractionChannelMessage,
  successReintegratedMessage,
} from '../../../../../../../BatchTicketRequests/utils';
import { ProveOrderOrigin } from '../../../../../../../NewApplication/Order/ProveOrderOrigin';
import { useBatchTicketResumeDetail } from '../../../../../../hooks/batchTicketResumeDetail';
import { updateBasketCustomerInteractionChannel } from '../../../../../../services';

export interface ProveBasketOriginProps {}

export const ProveBasketOrigin: React.FC<ProveBasketOriginProps> = () => {
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');
  const { hide, show } = useLoading();
  const { goBack } = useNavigation();
  const { showToast, showToastFromError } = useToastCreator('toast-update-interaction-channel');
  const { basketCustomerOrderItem } = useBatchTicketResumeDetail();
  const { updateBasketCustomerOrderItem } = useBatchTicketActions();

  const [isEditing, setIsEditing] = useState(false);
  const [hasSavedInteractionChannel, setHasSavedInteractionChannel] = useState(false);
  const [interactionChannelAudit, setInteractionChannelAudit] =
    useState<InteractionChannelAuditRequest | null>();

  const handleSubmit = async () => {
    if (!interactionChannelAudit) {
      return;
    }
    try {
      show({
        title: 'Aguarde estamos adicionando o canal de solicitação a ofertas deste cliente.',
        description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
      });

      const response = await updateBasketCustomerInteractionChannel({
        basketCustomerOrderId: basketCustomerOrderItem?.basketCustomerOrderId!,
        updateInteractionChannelAuditRequest: {
          interactionChannelAudit,
        },
      });

      updateBasketCustomerOrderItem(response);

      const customerOffers = response?.customerOffers;

      const allErrorsEmpty = customerOffers?.every((offer) =>
        offer?.basketOffers?.every(
          (basketOffer) => !basketOffer.errors || basketOffer?.errors?.length === 0
        )
      );

      const toastMessage = allErrorsEmpty
        ? successReintegratedMessage
        : successAddInteractionChannelMessage;

      showToast(...toastMessage);

      if (allErrorsEmpty) {
        return goBack();
      }
      setIsEditing(false);
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }
      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
    }
  };

  useEffect(() => {
    if (basketCustomerOrderItem) {
      setHasSavedInteractionChannel(!!basketCustomerOrderItem.interactionChannelAudit);
      setInteractionChannelAudit(basketCustomerOrderItem.interactionChannelAudit);
    }
  }, [basketCustomerOrderItem]);

  if (hasSavedInteractionChannel && !isEditing) {
    return (
      <Stack
        py="stack.xxxs"
        alignItems={isTablet ? 'initial' : 'center'}
        direction={isTablet ? 'column' : 'row'}
        space={isTablet ? 'stack.xxxs' : 0}>
        <HStack alignItems="center" space="inline.nano">
          <UI.Icon name="Success" size="xs" color="feedback.success.default" />
          <VStack>
            <UI.Text variant="semibold">Comprove a origem da ordem</UI.Text>
            <UI.Text color="neutral.dark" fontSize={Typography['fontsize.xs']}>
              Antes de enviar, informe a origem da boleta
            </UI.Text>
          </VStack>
        </HStack>
        {!isTablet && <Stack flex={1} />}
        <HStack justifyContent="space-between" flex={2}>
          <VStack space="stack.quarck">
            <UI.Text fontSize={Typography['fontsize.xs']}>Canal de solicitação</UI.Text>
            <UI.Text>
              {basketCustomerOrderItem?.interactionChannelAudit?.interactionChannelOption.channel ||
                interactionChannelAudit?.interactionChannelOptionValue}
            </UI.Text>
          </VStack>
          <Box w="1px" bg="neutral.lightest" />
          <VStack space="stack.quarck">
            <UI.Text fontSize={Typography['fontsize.xs']}>Data</UI.Text>
            <UI.Text>
              {Utils.Mask.DateFormat(interactionChannelAudit?.interactionChannelDate, 'dd/MM/yyyy')}
            </UI.Text>
          </VStack>
          <Box w="1px" bg="neutral.lightest" />
          <VStack space="stack.quarck">
            <UI.Text fontSize={Typography['fontsize.xs']}>Horário</UI.Text>
            <UI.Text>
              {Utils.Mask.DateFormat(interactionChannelAudit?.interactionChannelDate, 'HH:mm')}
            </UI.Text>
          </VStack>
          <UI.IconButton
            testId="edit-interaction-channel"
            icon="Edit"
            p={0}
            iconSize="xs"
            iconColor="brand.tertiary.principal"
            onPress={() => setIsEditing(true)}
          />
        </HStack>
      </Stack>
    );
  }

  return (
    <Box
      borderWidth={1}
      borderColor="neutral.lightest"
      borderRadius={BoxBorder.heavy}
      p="inset.xs"
      zIndex={10}>
      <ProveOrderOrigin
        hideSeparator
        handleUpdateInteractionChannelAudit={setInteractionChannelAudit}
        defaultInteractionChannel={basketCustomerOrderItem?.interactionChannelAudit}
        CustomHandleElement={
          <UI.Button isDisabled={!interactionChannelAudit} onPress={handleSubmit}>
            SALVAR ORIGEM
          </UI.Button>
        }
      />
    </Box>
  );
};
